<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';
import CaptainGenerationDetails from '../CaptainGenerationDetails.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';
import MessageApi from 'dashboard/api/inbox/message';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION, SENDER_TYPES } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  id,
  sender,
  senderType,
  conversationId,
  contentAttributes,
  currentUserId,
} = useMessageContext();
const { t } = useI18n();

const isCaptainMessage = computed(
  () =>
    (sender.value?.type ?? senderType.value) === SENDER_TYPES.CAPTAIN_ASSISTANT
);

const metaColorClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.PRIVATE
    ? 'text-n-amber-12/50'
    : 'text-n-slate-11'
);

const emailMetaClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : ''
);

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  if (!inReplyTo.value?.id) return;
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const replyAuthor = computed(() => {
  if (!inReplyTo.value) return '';
  const msg = inReplyTo.value;
  const isMsgOutgoing =
    msg.messageType === MESSAGE_VARIANTS.OUTGOING ||
    msg.message_type === 1 ||
    msg.messageType === 1 ||
    msg.sender?.type === 'User';

  if (isMsgOutgoing) {
    return 'Você';
  }

  return (
    msg.content_attributes?.sender_name ||
    msg.contentAttributes?.sender_name ||
    msg.content_attributes?.senderName ||
    msg.sender?.name ||
    'Contato'
  );
});

const replyAttachment = computed(() => {
  if (!inReplyTo.value?.attachments?.length) return null;
  const first = inReplyTo.value.attachments[0];
  const fileType = (first.fileType || first.file_type || '').toLowerCase();
  const url =
    first.dataUrl ||
    first.data_url ||
    first.fileUrl ||
    first.file_url ||
    first.thumbUrl ||
    first.thumb_url ||
    '';
  const isImage =
    fileType === 'image' ||
    fileType === 'sticker' ||
    url.includes('.webp') ||
    url.includes('.jpg') ||
    url.includes('.png');

  return {
    isImage,
    url,
  };
});

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo.value) return '';

  const msg = inReplyTo.value;
  const content = msg.content;
  const attachments = msg.attachments;
  const contentType = msg.contentType || msg.content_type;
  const isSticker =
    contentType === 'sticker' ||
    msg.content_attributes?.is_sticker ||
    msg.contentAttributes?.isSticker;

  if (isSticker) return '✨ Figurinha';

  const isAudio =
    contentType === 'audio' ||
    contentType === 'voice_call' ||
    (attachments?.length &&
      ['audio', 'voice_call'].includes(
        (attachments[0].fileType || attachments[0].file_type || '').toLowerCase()
      ));

  if (isAudio) return '🎤 Mensagem de áudio';

  if (content) return new MessageFormatter(content).formattedMessage;

  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = (
      firstAttachment.fileType ||
      firstAttachment.file_type ||
      ''
    ).toLowerCase();

    if (fileType === 'image' || fileType === 'sticker') return '🖼️ Imagem';
    if (fileType === 'video') return '🎥 Vídeo';
    if (fileType === 'file') return '📄 Documento';

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return 'Mensagem';
});

// Reações com Emojis (WhatsApp Reactions)
const reactionsList = computed(() => {
  const raw =
    contentAttributes?.value?.reactions ||
    contentAttributes?.value?.reaction ||
    {};
  if (typeof raw !== 'object' || !raw) return [];

  return Object.entries(raw)
    .filter(([emoji, users]) => emoji && Array.isArray(users) && users.length > 0)
    .map(([emoji, users]) => ({
      emoji,
      count: users.length,
      users,
      hasMyReaction:
        users.includes('agent') ||
        users.includes(String(currentUserId?.value || '')),
    }));
});

const toggleMyReaction = async emoji => {
  if (!id?.value || !conversationId?.value) return;
  try {
    await MessageApi.react(conversationId.value, id.value, emoji);
  } catch (err) {
    console.error('Erro ao alternar reação:', err);
  }
};
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <!-- Caixa de Resposta / Citação (Estilo WhatsApp) -->
    <div
      v-if="inReplyTo"
      class="rounded-md p-1.5 pl-2.5 mb-2 flex items-center justify-between gap-2 overflow-hidden cursor-pointer bg-black/25 dark:bg-black/35 hover:bg-black/40 transition-colors border-l-[3.5px] border-[#06b6d4] select-none text-xs"
      @click="scrollToMessage"
    >
      <div class="min-w-0 flex-1 pr-1">
        <div class="text-[11.5px] font-semibold text-[#06b6d4] truncate leading-tight mb-0.5">
          {{ replyAuthor }}
        </div>
        <div
          v-dompurify-html="replyToPreview"
          class="text-xs text-slate-200/90 line-clamp-1 leading-snug break-all"
        />
      </div>

      <!-- Thumbnail de mídia caso a mensagem citada tenha imagem/figurinha -->
      <div
        v-if="replyAttachment && replyAttachment.isImage && replyAttachment.url"
        class="w-8 h-8 rounded bg-black/40 flex-shrink-0 overflow-hidden flex items-center justify-center border border-white/10"
      >
        <img
          :src="replyAttachment.url"
          class="w-full h-full object-cover"
          alt="Mídia"
          loading="lazy"
        />
      </div>
    </div>
    <slot />
    <template v-if="shouldShowMeta">
      <CaptainGenerationDetails
        v-if="isCaptainMessage"
        :message-id="id"
        class="mt-2"
      >
        <template #meta>
          <MessageMeta :class="[emailMetaClass, metaColorClass]" />
        </template>
      </CaptainGenerationDetails>
      <MessageMeta
        v-else
        :class="[flexOrientationClass, emailMetaClass, metaColorClass]"
        class="mt-2"
      />
    </template>

    <!-- Pílulas de Reações com Emojis (Estilo WhatsApp) -->
    <div
      v-if="reactionsList.length"
      class="flex items-center gap-1.5 -mb-1 mt-1.5 select-none flex-wrap"
      :class="orientation === ORIENTATION.RIGHT ? 'justify-end' : 'justify-start'"
    >
      <button
        v-for="reaction in reactionsList"
        :key="reaction.emoji"
        type="button"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#111b21] dark:bg-[#182229] border border-slate-700/60 shadow-xs text-xs cursor-pointer transition-transform hover:scale-110 active:scale-95"
        :class="reaction.hasMyReaction ? 'border-emerald-500/50 bg-emerald-950/20' : ''"
        :title="`Reagido por: ${reaction.users.join(', ')}`"
        @click.stop="toggleMyReaction(reaction.emoji)"
      >
        <span class="text-sm leading-none">{{ reaction.emoji }}</span>
        <span
          v-if="reaction.count > 1"
          class="text-[10px] font-semibold text-slate-300 font-mono"
        >
          {{ reaction.count }}
        </span>
      </button>
    </div>
  </div>
</template>
